import { createVNode as _createVNode } from "vue";
/**
 * @author xiezhixiong
 * @fileoverview 对战表
 */
import { defineComponent, computed } from "vue";
import { calculateColNum, calculateRowNum, createArray } from "./utils";
import winUrl from "@/assets/img/championship/pc/win.png";
export default defineComponent({
  name: "TeamInfoPopup",
  props: {
    // 队伍总数
    teamNum: {
      type: Number,
      required: true,
      default: 0
    },
    /**
     * 队伍信息
     * eg: {
     *    name: '队伍名称',
     *    members: [
     *      { name: '名称', career: '门派', level: '等级' },
     *      { name: '名称', career: '门派', level: '等级' },
     *    ],
     * }
     */
    teams: {
      type: Array,
      default() {
        return [];
      }
    },
    /**
     * 棋盘格子高度，默认 24
     */
    gridHeight: {
      type: Number,
      default: 24
    },
    /**
     * 棋盘格子宽度，默认 148
     */
    gridWidth: {
      type: Number,
      default: 148
    },
    /**
     * 棋子宽度，默认 114
     */
    pieceWidth: {
      type: Number,
      default: 114
    },
    /**
     * 棋子高度，默认 41
     */
    pieceHeight: {
      type: Number,
      default: 41
    },
    /**
     * 是否反方向的，默认 false
     */
    reverse: {
      type: Boolean,
      default() {
        return false;
      }
    },
    /**
     * 是否显示棋格，默认 false
     */
    chessboard: {
      type: Boolean,
      default() {
        return false;
      }
    },
    /**
     * 容器高度，默认 '100%'
     */
    height: {
      type: String,
      default() {
        return "100%";
      }
    },
    /**
     * 容器宽度，默认 '100%'
     */
    width: {
      type: String,
      default() {
        return "100%";
      }
    },
    /**
     * 相对左上角坐标整体偏移值
     */
    offset: {
      type: Object,
      default() {
        return {
          top: 0,
          left: 0
        };
      }
    },
    /**
     * 是否显示高亮的描边，默认 false
     */
    stroke: {
      type: Boolean,
      default() {
        return false;
      }
    }
  },
  emits: ["select"],
  setup(props, {
    emit
  }) {
    const col = computed(() => calculateColNum(props.teamNum));
    const row = computed(() => calculateRowNum(props.teamNum));
    const columns = computed(() => createArray(col.value));
    const rows = computed(() => createArray(row.value));
    const incomplete = computed(() => props.teams.length < col.value);
    const handleSelect = item => {
      emit("select", item);
    };
    return () => {
      var _props$teams;
      const providedOffsetTop = props.offset.top || 0;
      const providedOffsetLeft = props.offset.left || 0;

      /**
       * 计算每个棋子的位置
       * @param {*} rowIndex 行索引
       * @param {*} colIndex 列索引
       * @param {*} rowNum 总行数
       * @param {*} teamsLen 当前场次的队伍数量，不是总的
       * @param {*} colNum 总列数
       */
      const calculatePieceRect = (rowIndex, colIndex, rowNum, teamsLen, colNum) => {
        const halfPieceHeight = -Math.ceil(props.pieceHeight / 2);
        // 跳步
        const skip = Math.pow(2, colIndex) - 1;
        // 格子之间的间隔
        const gutter = Math.floor(rowNum / teamsLen);
        const offset = (skip + rowIndex * (gutter + 1)) * props.gridHeight;
        const top = halfPieceHeight + offset;
        // 处理反向逻辑
        const providedColIndex = !props.reverse ? colIndex : colNum - colIndex - 1;
        const left = (props.gridWidth - props.pieceWidth) / 2 + providedColIndex * props.gridWidth;
        return {
          top: `${top + providedOffsetTop}px`,
          left: `${left + providedOffsetLeft}px`
        };
      };

      /**
       * 计算每个线条的位置
       * @param {*} rowIndex 行索引
       * @param {*} colIndex 列索引
       * @param {*} rowNum 总行数
       * @param {*} teamsLen 当前场次的队伍数量，不是总的
       * @param {*} colNum 总列数
       */
      const calculateLineRect = (rowIndex, colIndex, rowNum, teamsLen, colNum) => {
        // 跳步
        const skip = Math.pow(2, colIndex) - 1;
        // 格子之间的间隔
        const gutter = Math.floor(rowNum / (teamsLen / 2));
        const offset = (skip + rowIndex * (gutter + 1)) * props.gridHeight;
        const top = offset;
        // 处理反向逻辑
        const providedColIndex = !props.reverse ? colIndex : colNum - colIndex - 2;
        const left = props.gridWidth / 2 + providedColIndex * props.gridWidth;
        const height = (Math.floor(gutter / 2) + 1) * props.gridHeight;
        return {
          top: `${top + providedOffsetTop}px`,
          left: `${left + providedOffsetLeft}px`,
          width: `${props.gridWidth}px`,
          height: `${height}px`
        };
      };

      /**
       * 计算额外线条的位置
       * @param {*} rowIndex 行索引
       * @param {*} colIndex 列索引
       * @param {*} rowNum 总行数
       * @param {*} teamsLen 当前场次的队伍数量，不是总的
       * @param {*} colNum 总列数
       */
      const calculateExtraLineRect = (rowIndex, colIndex, rowNum, teamsLen, colNum) => {
        // 跳步
        const skip = Math.pow(2, colIndex) - 1;
        // 格子之间的间隔
        const gutter = rowNum - skip * 2;
        const offset = (skip + rowIndex * (gutter + 1)) * props.gridHeight;
        const top = offset;
        // 处理反向逻辑
        const providedColIndex = !props.reverse ? colIndex : colNum - colIndex - 2;
        const left = props.gridWidth / 2 + providedColIndex * props.gridWidth;
        const height = gutter * props.gridHeight;
        return {
          top: `${top + providedOffsetTop}px`,
          left: `${left + providedOffsetLeft}px`,
          width: `${props.gridWidth}px`,
          height: `${height}px`
        };
      };
      return _createVNode("div", {
        "class": ["war-schedule", props.reverse ? "war-schedule__reverse" : ""],
        "style": {
          width: props.width,
          height: props.height
        }
      }, [props.chessboard && _createVNode("div", {
        "class": "war-schedule-chessboard"
      }, [columns.value.map(item => _createVNode("ul", {
        "class": "war-schedule-chessboard-column"
      }, [rows.value.map(rowsItem => _createVNode("li", {
        "key": `${item}_${rowsItem}`,
        "class": "war-schedule-chessboard-item",
        "style": {
          width: `${props.gridWidth}px`,
          height: `${props.gridHeight}px`
        }
      }, null))]))]), _createVNode("div", {
        "class": "war-schedule-chessboard-cover"
      }, [(_props$teams = props.teams) === null || _props$teams === void 0 ? void 0 : _props$teams.map((item, colIndex) => {
        var _props$teams2;
        const isLast = colIndex === ((_props$teams2 = props.teams) === null || _props$teams2 === void 0 ? void 0 : _props$teams2.length) - 1;
        const inCompleteLast = incomplete.value && isLast;
        return _createVNode("div", {
          "class": "war-schedule-chessboard-col"
        }, [item.map((sessionItem, sessionIndex) => {
          var _sessionItem$redTeam, _sessionItem$blueTeam, _sessionItem$redTeam2, _sessionItem$redTeam3, _sessionItem$blueTeam2, _sessionItem$blueTeam3, _sessionItem$redTeam4;
          let rowIndex = sessionIndex * 2;
          // 判断是否是最后一场数据，并且是不是产生了胜者
          {
            /* const isChampion =
            colIndex === props.teams?.length - 1
            && sessionItem.winner === sessionItem.redTeam?.teamId; */
          }
          // if (colIndex === 2) {
          //   console.log("colIndex:", sessionItem);
          // }

          const alone = !((_sessionItem$redTeam = sessionItem.redTeam) !== null && _sessionItem$redTeam !== void 0 && _sessionItem$redTeam.teamId) || !((_sessionItem$blueTeam = sessionItem.blueTeam) !== null && _sessionItem$blueTeam !== void 0 && _sessionItem$blueTeam.teamId);
          return _createVNode("div", {
            "key": `${colIndex}_${rowIndex}`,
            "class": "war-schedule-chessboard-group"
          }, [_createVNode("div", {
            "class": ["war-schedule-chessboard-piece", sessionItem.winner === sessionItem.redTeam.teamId || colIndex == 2 && sessionItem.redTeam.name || !sessionItem.winner ? "war-schedule-chessboard-piece__red" : "war-schedule-chessboard-piece__blue"],
            "style": {
              ...calculatePieceRect(rowIndex, colIndex, row.value, !alone || incomplete.value ? item.length * 2 : 1, col.value)
            },
            "onClick": () => handleSelect(sessionItem.redTeam)
          }, [_createVNode("div", {
            "class": "war-schedule-chessboard-piece-content"
          }, [sessionItem.winner === sessionItem.redTeam.teamId || colIndex == 2 && sessionItem.redTeam.name ? _createVNode("img", {
            "class": "war-schedule-chessboard-piece-img",
            "src": winUrl
          }, null) : "", _createVNode("span", {
            "class": "war-schedule-chessboard-piece-name"
          }, [(_sessionItem$redTeam2 = sessionItem.redTeam) === null || _sessionItem$redTeam2 === void 0 ? void 0 : _sessionItem$redTeam2.name]), _createVNode("span", {
            "class": [colIndex == 2 ? "war-schedule-chessboard-piece-hide" : "war-schedule-chessboard-piece-score"]
          }, [(_sessionItem$redTeam3 = sessionItem.redTeam) === null || _sessionItem$redTeam3 === void 0 ? void 0 : _sessionItem$redTeam3.wins])])]), !alone && _createVNode("div", {
            "class": ["war-schedule-chessboard-piece", sessionItem.winner === sessionItem.blueTeam.teamId || colIndex == 2 && sessionItem.blueTeam.name || !sessionItem.winner ? "war-schedule-chessboard-piece__red" : "war-schedule-chessboard-piece__blue"],
            "style": {
              ...calculatePieceRect(rowIndex + 1, colIndex, row.value, !alone || incomplete.value ? item.length * 2 : 1, col.value)
            },
            "onClick": () => handleSelect(sessionItem.blueTeam)
          }, [_createVNode("div", {
            "class": "war-schedule-chessboard-piece-content"
          }, [sessionItem.winner === sessionItem.blueTeam.teamId || colIndex == 2 && sessionItem.blueTeam.name ? _createVNode("img", {
            "class": "war-schedule-chessboard-piece-img",
            "src": winUrl
          }, null) : "", _createVNode("span", {
            "class": "war-schedule-chessboard-piece-name"
          }, [(_sessionItem$blueTeam2 = sessionItem.blueTeam) === null || _sessionItem$blueTeam2 === void 0 ? void 0 : _sessionItem$blueTeam2.name]), _createVNode("span", {
            "class": [colIndex == 2 ? "war-schedule-chessboard-piece-hide" : "war-schedule-chessboard-piece-score"]
          }, [(_sessionItem$blueTeam3 = sessionItem.blueTeam) === null || _sessionItem$blueTeam3 === void 0 ? void 0 : _sessionItem$blueTeam3.wins])])]), (!isLast || inCompleteLast) && _createVNode("span", {
            "class": ["war-schedule-chessboard-line", sessionItem.winner && props.stroke ? sessionItem.winner === ((_sessionItem$redTeam4 = sessionItem.redTeam) === null || _sessionItem$redTeam4 === void 0 ? void 0 : _sessionItem$redTeam4.teamId) || colIndex == 2 && sessionItem.redTeam.name ? "war-schedule-chessboard-line__win1" : "war-schedule-chessboard-line__win2" : "", props.reverse ? "war-schedule-chessboard-line__reverse" : "", inCompleteLast ? "war-schedule-chessboard-line__incomplete" : "", props.stroke ? "war-schedule-chessboard-line__stroke" : ""],
            "style": {
              ...calculateLineRect(sessionIndex, colIndex, row.value, !isLast || inCompleteLast ? item.length * 2 : 1, col.value)
            }
          }, [_createVNode("i", null, null), _createVNode("b", null, null)])]);
        }), inCompleteLast && _createVNode("span", {
          "class": ["war-schedule-chessboard-line", props.reverse ? "war-schedule-chessboard-line__reverse" : "", props.stroke ? "war-schedule-chessboard-line__stroke" : ""],
          "style": {
            ...calculateExtraLineRect(0, colIndex, row.value, item.length * 2, col.value)
          }
        }, [_createVNode("i", null, null), _createVNode("b", null, null)])]);
      })])]);
    };
  }
});